import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  MousePointer,
  Calendar,
  Smartphone,
  Monitor,
  Apple,
  Bug,
  ExternalLink,
  Copy,
  Check,
  Download,
  Mail,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { linksAPI } from '../services/api';
import { copyToClipboard, formatDate } from '../utils/helpers';
import Button from './common/Button';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import DetailedAnalyticsTable from './DetailedAnalyticsTable';
import tokenService from '../services/tokenService';
import { SHORT_LINK_BASE_URL } from '../config';

const StatCard = ({ icon: Icon, label, value, accent = 'brand', sub }) => {
  const accents = {
    brand: 'from-brand-500 to-violet-600',
    emerald: 'from-emerald-500 to-teal-600',
    sky: 'from-sky-500 to-indigo-600',
    rose: 'from-rose-500 to-pink-600',
    amber: 'from-amber-500 to-orange-500',
    ink: 'from-ink-700 to-ink-900',
  };
  return (
    <div className="rounded-2xl border border-ink-200 bg-white shadow-soft p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-ink-500">
            {label}
          </p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-ink-900">
            {value}
          </p>
          {sub && <p className="mt-1 text-xs text-ink-500">{sub}</p>}
        </div>
        <span
          className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${accents[accent]} text-white shadow-soft`}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
};

const Analytics = () => {
  const { shortCode } = useParams();
  const { loading, error, executeApiCall, clearError } = useApi();
  const [analytics, setAnalytics] = useState(null);
  const [tech, setTech] = useState(null);
  const [copied, setCopied] = useState(null);
  const [reportState, setReportState] = useState('idle'); // idle | sending | sent

  const fetchAnalytics = useCallback(async () => {
    const result = await executeApiCall(linksAPI.getAnalytics, shortCode);
    if (result.success) setAnalytics(result.data);
  }, [shortCode, executeApiCall]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    linksAPI.getTechBreakdown(shortCode).then((r) => {
      if (r && r.success) setTech(r.data);
    });
  }, [shortCode]);

  const handleEmailReport = async () => {
    setReportState('sending');
    const r = await linksAPI.emailReport(7);
    setReportState(r && r.success ? 'sent' : 'idle');
  };

  // CSV export is auth-protected, so fetch with the token and download the blob.
  const handleExportCsv = async () => {
    try {
      const res = await fetch(linksAPI.analyticsCsvUrl(shortCode), {
        headers: { Authorization: `Bearer ${tokenService.getAccessToken()}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${shortCode}-analytics.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // no-op
    }
  };

  const handleCopy = async (text, key) => {
    const r = await copyToClipboard(text);
    if (r.success) {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" text="Loading analytics…" />
      </div>
    );
  }

  if (error) {
    const isAuthError =
      error.includes('token') ||
      error.includes('unauthorized') ||
      error.includes('401');

    return (
      <div className="max-w-4xl mx-auto">
        <Alert
          type="error"
          message={error}
          onClose={clearError}
          className="mb-6"
        />

        {isAuthError && (
          <div className="rounded-2xl border border-ink-200 bg-white p-6 shadow-soft mb-6">
            <h3 className="text-base font-semibold text-ink-900">
              You may need to sign back in
            </h3>
            <p className="mt-1.5 text-sm text-ink-500">
              Your session may have expired. Sign in again to view analytics.
            </p>
            <div className="mt-4 flex gap-2">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800 transition"
              >
                Sign in
              </Link>
              <Button
                variant="secondary"
                onClick={() => {
                  tokenService.clearTokens();
                  window.location.href = '/login';
                }}
              >
                Clear session & sign in
              </Button>
            </div>
          </div>
        )}

        <Link
          to="/links"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back to links
        </Link>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16 rounded-2xl border border-ink-200 bg-white">
        <h3 className="text-lg font-semibold text-ink-900">
          Analytics not found
        </h3>
        <p className="mt-1 text-sm text-ink-500">
          This link doesn't exist or has been deleted.
        </p>
        <Link
          to="/links"
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2 text-sm font-medium text-white"
        >
          Back to links
        </Link>
      </div>
    );
  }

  const shortUrl = `${SHORT_LINK_BASE_URL}/r/${analytics.short_code}`;
  const debugUrl = `${shortUrl}?d=1`;

  return (
    <div className="max-w-5xl mx-auto">
      <Link
        to="/links"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-ink-700"
      >
        <ArrowLeft className="h-4 w-4" /> Back to links
      </Link>

      <header className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
            {analytics.title || 'Link analytics'}
          </h1>
          <a
            href={shortUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-mono"
          >
            {shortUrl}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => handleCopy(shortUrl, 'short')}
          >
            {copied === 'short' ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied === 'short' ? 'Copied' : 'Copy URL'}
          </Button>
          <Button variant="secondary" onClick={handleExportCsv}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button
            variant="secondary"
            onClick={handleEmailReport}
            disabled={reportState !== 'idle'}
          >
            <Mail className="h-4 w-4" />
            {reportState === 'sent' ? 'Report sent' : reportState === 'sending' ? 'Sending…' : 'Email report'}
          </Button>
        </div>
      </header>

      {/* Stat cards */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          icon={MousePointer}
          label="Total clicks"
          value={(analytics.total_clicks || 0).toLocaleString()}
          accent="brand"
        />
        <StatCard
          icon={Users}
          label="Unique visitors"
          value={(analytics.unique_clicks || 0).toLocaleString()}
          accent="emerald"
        />
        <StatCard
          icon={Calendar}
          label="Created"
          value={formatDate(analytics.created_at)}
          accent="sky"
        />
        <StatCard
          icon={Smartphone}
          label="Mobile share"
          value={`${
            analytics.total_clicks
              ? Math.round(
                  (((analytics.android_clicks || 0) +
                    (analytics.ios_clicks || 0)) /
                    analytics.total_clicks) *
                    100
                )
              : 0
          }%`}
          sub={`${(analytics.android_clicks || 0) + (analytics.ios_clicks || 0)} mobile clicks`}
          accent="rose"
        />
      </div>

      {/* Platform breakdown */}
      <div className="mt-8 rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
        <h2 className="text-sm font-semibold text-ink-900">
          Platform breakdown
        </h2>
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          {[
            {
              icon: Monitor,
              label: 'Desktop',
              value: analytics.desktop_clicks || 0,
              accent: 'bg-ink-100 text-ink-700',
            },
            {
              icon: Smartphone,
              label: 'Android',
              value: analytics.android_clicks || 0,
              accent: 'bg-emerald-100 text-emerald-700',
            },
            {
              icon: Apple,
              label: 'iOS',
              value: analytics.ios_clicks || 0,
              accent: 'bg-sky-100 text-sky-700',
            },
          ].map((p) => {
            const total = analytics.total_clicks || 0;
            const pct = total ? Math.round((p.value / total) * 100) : 0;
            return (
              <div
                key={p.label}
                className="rounded-xl border border-ink-200 bg-ink-50/40 p-4"
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${p.accent}`}
                  >
                    <p.icon className="h-4 w-4" />
                  </span>
                  <span className="text-xs font-medium text-ink-500">
                    {pct}%
                  </span>
                </div>
                <p className="mt-3 text-2xl font-bold tracking-tight text-ink-900">
                  {p.value.toLocaleString()}
                </p>
                <p className="text-xs text-ink-500">{p.label}</p>
                <div className="mt-3 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-brand-500 to-violet-600 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Browser & OS breakdown + bot note */}
      <div className="mt-8 grid md:grid-cols-2 gap-5">
        {[
          { title: 'Top browsers', rows: tech?.browsers, key: 'browser' },
          { title: 'Operating systems', rows: tech?.operatingSystems, key: 'os' },
        ].map((panel) => {
          const rows = panel.rows || [];
          const max = rows.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;
          return (
            <div key={panel.title} className="rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
              <h2 className="text-sm font-semibold text-ink-900">{panel.title}</h2>
              {rows.length === 0 ? (
                <p className="mt-4 text-sm text-ink-500">No data yet.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {rows.map((r) => (
                    <li key={r[panel.key]}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-ink-800">{r[panel.key] || 'Unknown'}</span>
                        <span className="text-ink-500">{(r.count || 0).toLocaleString()}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-sky-500 to-indigo-600 rounded-full"
                          style={{ width: `${Math.round(((r.count || 0) / max) * 100)}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {analytics.bot_clicks > 0 && (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-ink-200 bg-ink-50/60 px-4 py-3 text-sm text-ink-600">
          <Bug className="h-4 w-4 text-amber-600" />
          {analytics.bot_clicks.toLocaleString()} bot/crawler hit{analytics.bot_clicks === 1 ? '' : 's'} excluded from the counts above.
        </div>
      )}

      {/* Link details */}
      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
          <h2 className="text-sm font-semibold text-ink-900">Link details</h2>
          <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wider text-ink-500">
                Destination
              </dt>
              <dd className="mt-0.5 text-ink-800 break-all">
                <a
                  href={analytics.original_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-brand-700"
                >
                  {analytics.original_url}
                </a>
              </dd>
            </div>
            {analytics.title && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">
                  Title
                </dt>
                <dd className="mt-0.5 text-ink-800">{analytics.title}</dd>
              </div>
            )}
            {analytics.description && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">
                  Description
                </dt>
                <dd className="mt-0.5 text-ink-800">{analytics.description}</dd>
              </div>
            )}
          </dl>

          {(analytics.android_package_name || analytics.ios_bundle_id) && (
            <div className="mt-6 pt-6 border-t border-ink-100">
              <h3 className="text-sm font-semibold text-ink-900 mb-3">
                Mobile configuration
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {analytics.android_package_name && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                      <Smartphone className="h-3.5 w-3.5" /> Android
                    </p>
                    <p className="mt-2 font-mono text-xs text-emerald-900 break-all">
                      {analytics.android_package_name}
                    </p>
                    {analytics.android_fallback_url && (
                      <p className="mt-1 text-xs text-emerald-700 break-all">
                        Fallback: {analytics.android_fallback_url}
                      </p>
                    )}
                  </div>
                )}
                {analytics.ios_bundle_id && (
                  <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-sky-700 flex items-center gap-1.5">
                      <Apple className="h-3.5 w-3.5" /> iOS
                    </p>
                    <p className="mt-2 font-mono text-xs text-sky-900 break-all">
                      {analytics.ios_bundle_id}
                    </p>
                    {analytics.ios_fallback_url && (
                      <p className="mt-1 text-xs text-sky-700 break-all">
                        Fallback: {analytics.ios_fallback_url}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-soft">
          <div className="inline-flex items-center gap-2 text-amber-700 font-semibold text-sm">
            <Bug className="h-4 w-4" /> Debug
          </div>
          <p className="mt-1.5 text-sm text-amber-700/90">
            Append <code className="font-mono">?d=1</code> to see platform
            detection in action.
          </p>
          <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 font-mono text-xs text-ink-800 break-all">
            {debugUrl}
          </div>
          <Button
            variant="secondary"
            size="small"
            className="mt-3"
            onClick={() => handleCopy(debugUrl, 'debug')}
          >
            {copied === 'debug' ? (
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied === 'debug' ? 'Copied' : 'Copy debug URL'}
          </Button>
        </div>
      </div>

      <div className="mt-8">
        <DetailedAnalyticsTable shortCode={shortCode} />
      </div>
    </div>
  );
};

export default Analytics;
